import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { BuildingsIcon, UsersThreeIcon, TruckIcon, PackageIcon, WrenchIcon, ClipboardTextIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

function Stat({ label, value, icon: Icon, tone = "amber" }) {
  const tones = {
    amber: "from-amber-400/20 to-orange-500/5 border-blue-500/30 text-blue-700",
    blue: "from-blue-500/20 to-blue-600/5 border-blue-500/30 text-blue-300",
    green: "from-emerald-500/20 to-emerald-600/5 border-emerald-500/30 text-emerald-300",
    violet: "from-violet-500/20 to-violet-600/5 border-violet-500/30 text-violet-300",
  };
  return (
    <div className={`p-6 bg-gradient-to-br ${tones[tone]} border rounded-sm`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">{label}</div>
          <div className="font-display text-4xl font-black tabular-nums text-slate-900 mt-2">{value ?? "—"}</div>
        </div>
        {Icon && <Icon size={22} weight="bold" className={tones[tone].split(" ").pop()} />}
      </div>
    </div>
  );
}

export default function SuperAdminOverview() {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/super-admin/stats"),
          api.get("/super-admin/companies"),
        ]);
        setStats(s.data);
        setCompanies(c.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <div data-testid="sa-overview">
      <div className="border-b border-slate-300 px-8 py-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-blue-600 font-bold mb-1">/// Platform</div>
        <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Overview</h1>
        <p className="text-sm text-slate-400 mt-1">Global stats across every tenant in Rep-Fleet.</p>
      </div>

      <div className="p-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Companies" value={stats?.total_companies} icon={BuildingsIcon} tone="amber" />
          <Stat label="Users" value={stats?.total_users} icon={UsersThreeIcon} tone="blue" />
          <Stat label="Trucks" value={stats?.total_trucks} icon={TruckIcon} tone="green" />
          <Stat label="Trailers" value={stats?.total_trailers} icon={PackageIcon} tone="violet" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Stat label="Repairs Logged" value={stats?.total_repairs} icon={WrenchIcon} tone="amber" />
          <Stat label="Jobs Created" value={stats?.total_jobs} icon={ClipboardTextIcon} tone="blue" />
          <Stat label="Active Companies" value={stats?.active_companies} icon={BuildingsIcon} tone="green" />
        </div>

        <div className="bg-white border border-slate-300 rounded-sm">
          <div className="px-6 py-4 border-b border-slate-300 flex items-center justify-between">
            <div>
              <div className="font-display text-xl font-black tracking-tight text-slate-900">Recent Companies</div>
              <div className="text-xs text-slate-500 mt-0.5">Tenants provisioned on the platform</div>
            </div>
            <Link
              to="/super-admin/companies"
              className="text-xs text-blue-600 hover:text-blue-700 font-semibold uppercase tracking-wider font-mono"
              data-testid="sa-see-all-companies"
            >
              See all →
            </Link>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr className="text-left">
                <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-400">Name</th>
                <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-400">Plan</th>
                <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-400">Users</th>
                <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-400">Trucks</th>
                <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-400">Status</th>
              </tr>
            </thead>
            <tbody>
              {companies.slice(0, 8).map((c) => (
                <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`sa-recent-${c.id}`}>
                  <td className="px-6 py-3 font-semibold text-slate-900">{c.name}</td>
                  <td className="px-6 py-3 font-mono text-xs text-blue-700 uppercase">{c.plan}</td>
                  <td className="px-6 py-3 tabular-nums text-slate-600">{c.user_count}</td>
                  <td className="px-6 py-3 tabular-nums text-slate-600">{c.truck_count}</td>
                  <td className="px-6 py-3">
                    {c.is_active === false ? (
                      <span className="px-2 py-0.5 text-[10px] font-mono uppercase bg-red-500/20 text-red-300 border border-red-500/40 rounded-sm">Inactive</span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] font-mono uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-sm">Active</span>
                    )}
                  </td>
                </tr>
              ))}
              {companies.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-500 text-sm">No companies yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
